import * as React from "react";
import { useEffect, useState, useMemo } from "react";
import { SmartApplyTemplate } from "../../../template/smart-apply/default-template";
import { Link as RouterLink, useHistory, useLocation } from "react-router-dom";
import { Loader } from "../../../organisms/common/loading";
import { useModal } from "../../../../utils/custom-hooks";
import { BaseModal } from "../../../atoms/base-modal";
import { ProcedureStatusTag } from "../../../molecules/procedure-status-tag";
import { formatDatetime } from "../../../../utils/date-utils";
import { ProcedureCreateForm } from "../../../organisms/smart-apply/procedure-edit/create-form";
import {
  ApplicantSchemaKeysMigrator,
  createInitialCloudGovSchema,
  PublishStatusList,
} from "@graffer-inc/cloud-gov-core";
import { SmartApplyR } from "../route";
import {
  connectPageService,
  GrafferManagedTag,
  reportAsyncError,
} from "@graffer-inc/smart-apply";
import { ProcedureImportFromZipFileModal } from "../../../organisms/smart-apply/modals/procedure-import-from-zip-modal";
import { UpdateProcedureGroupModal } from "../../../organisms/smart-apply/modals/update-procedure-group-modal";
import { useAuthContextV2 } from "../../../../auth-state";
import { parseQueryOfLocation } from "../../../../utils/url-utils";
import {
  ProceduresContextProps,
  ProceduresService,
} from "../../../../application/service/smart-apply/procedures/procedures-service";
import { StatusFilterDropDown } from "../../../organisms/smart-apply/procedure-list/status-filter-dropdown";
import { matchType } from "../../../../application/domain/base/base-type";
import {
  InformationBanner,
  Link,
  Pagination,
  Table,
  TableBody,
  TableHeader,
  TableRow,
  Td,
  Th,
} from "@graffer-inc/console-design-kit";
import { SmartApplyMenuList } from "../../../organisms/smart-apply/menu-list";
import { ProceduresListHeader } from "./contentsHeader";
import classnames from "classnames";
import "./procedures.scss";

export const ProceduresList: React.FC<ProceduresContextProps> = ({
  isLoading,
  procedures,
  paging,
  setPage,
  movePage,
  queryName,
  setName,
  fetchProcedures,
  createProcedure,
  searchProcedures,
  checkProcedureImportZipFile,
  importProcedureFromZipFile,
  onDownloadCsvForUpdateGroup,
  bulkUpdateGroupPreview,
  bulkUpdateGroup,
  selectedStatus,
  setSelectedStatus,
}) => {
  const authState = useAuthContextV2();
  const history = useHistory();
  const { page, name } = parseQueryOfLocation(useLocation()) as {
    page: string | undefined;
    name: string | undefined;
  };
  const [isImportLoading, setIsImportLoading] = useState<boolean>(false);
  const procedureModal = useModal();
  const menuToggle = useModal();
  const importProcedureModal = useModal();
  const updateProcedureGroupModal = useModal();

  const authDefaultGroupId = useMemo(() => {
    if (
      !authState.auth.hasRole("Admin") &&
      authState.auth.groupIDs &&
      authState.auth.groupIDs.length > 0
    ) {
      return authState.auth.groupIDs[0];
    }
    return undefined;
  }, [authState.auth]);

  useEffect(() => {
    setPage(parseInt(page!, 10) || 1);
    setName(name || "");
    fetchProcedures();
  }, [page, name, setPage, setName, fetchProcedures, selectedStatus]);

  return (
    <SmartApplyTemplate
      breadcrumbs={[{ to: "#", title: "手続き一覧" }]}
      menuList={<SmartApplyMenuList />}
      contentsHeader={
        <ProceduresListHeader
          {...{
            setPage,
            queryName,
            setName,
            fetchProcedures,
            searchProcedures,
            selectedStatus,
            importProcedureModal,
            procedureModal,
            menuToggle,
            updateProcedureGroupModal,
          }}
        />
      }
    >
      <div className="procedures">
        <Table borderType="row" wrapper={false}>
          <TableHeader>
            <Th>
              <StatusFilterDropDown
                selectedStatus={selectedStatus}
                onSelect={setSelectedStatus}
              >
                公開状況
              </StatusFilterDropDown>
            </Th>
            <Th>受付開始日</Th>
            <Th>手続き名称</Th>
            <Th>受付終了日</Th>
            <Th>手続きID</Th>
          </TableHeader>
          <TableBody>
            <Loader isLoading={isLoading}>
              {procedures.map((a) => (
                <TableRow
                  key={a.id}
                  onClick={() =>
                    history.push(SmartApplyR.ProcedureDetail.link({ id: a.id }))
                  }
                >
                  <Td>
                    <ProcedureStatusTag status={a.publishStatus} />
                  </Td>
                  <Td>
                    {a.publishSetting.startAcceptAt
                      ? formatDatetime(a.publishSetting.startAcceptAt)
                      : "設定なし"}
                  </Td>
                  <Td>
                    {a.name}
                    {a.isGrafferManaged && (
                      <>
                        &nbsp; &nbsp;
                        <GrafferManagedTag />
                      </>
                    )}
                  </Td>
                  <Td>
                    {a.publishSetting.endAcceptAt
                      ? formatDatetime(a.publishSetting.endAcceptAt)
                      : "設定なし"}
                  </Td>
                  <Td>
                    <Link
                      anchor={({ ...props }) => (
                        <RouterLink
                          {...props}
                          onClick={(e) => e.stopPropagation()}
                          to={SmartApplyR.ProcedureDetail.link({ id: a.id })}
                        />
                      )}
                    >
                      {a.id}
                    </Link>
                  </Td>
                </TableRow>
              ))}
            </Loader>
          </TableBody>
        </Table>
        {!isLoading &&
          procedures.length === 0 &&
          matchType(
            name ||
              selectedStatus.length > 0 ||
              selectedStatus.length === PublishStatusList.length
              ? "no_results"
              : "no_registered",
            {
              no_registered: (
                <div className="py-2">
                  <InformationBanner
                    message="手続きがまだ登録されていません。"
                    description="「新規登録」より、手続きを登録してください。"
                  />
                </div>
              ),
              no_results: (
                <div className="py-2">
                  <InformationBanner
                    message="条件に該当する手続きはありませんでした。"
                    description="検索条件を確認してください。"
                  />
                </div>
              ),
            },
          )}
        {paging.total > 0 && (
          <div className={classnames("procedures-pagination-footer")}>
            <Pagination
              onPageClick={movePage}
              currentPage={paging.current}
              lastPage={paging.total}
            />
          </div>
        )}
      </div>
      <BaseModal
        hasOverflow={true}
        isActive={procedureModal.isActive}
        title="新規手続き作成"
        onClose={procedureModal.close}
      >
        <ProcedureCreateForm
          initialValues={{
            name: "",
            acceptAnyFile: false,
            requireAuthLevel: {
              individual: "NONE" as const,
              corporation: "NONE" as const,
            },
            applicantType: [],
            groupId: authDefaultGroupId,
          }}
          onSubmit={async (values) => {
            try {
              const formSchema = createInitialCloudGovSchema(
                values.applicantType,
              );
              const applicantSchemaKeys =
                ApplicantSchemaKeysMigrator.up(formSchema);
              if (applicantSchemaKeys instanceof Error) {
                throw applicantSchemaKeys;
              }
              await createProcedure({
                ...values,
                template: {
                  formSchema,
                },
                applicantSchemaKeys,
              });
            } catch (e) {
              reportAsyncError(e);
            } finally {
              procedureModal.close();
            }
          }}
          onClose={procedureModal.close}
        />
      </BaseModal>
      <ProcedureImportFromZipFileModal
        isActive={importProcedureModal.isActive}
        isLoading={isImportLoading}
        groupId={authDefaultGroupId}
        close={importProcedureModal.close}
        checkImportFile={checkProcedureImportZipFile}
        onSave={async (zipFile, shallClearCustomPath, groupId) => {
          setIsImportLoading(true);
          try {
            const importedId = await importProcedureFromZipFile(
              zipFile,
              shallClearCustomPath,
              groupId,
            );
            importProcedureModal.close();
            history.push(
              SmartApplyR.ProcedureDetail.link({
                id: importedId,
              }),
            );
          } finally {
            // エラーハンドリングは呼び出し元で行う
            setIsImportLoading(false);
          }
        }}
      />
      {updateProcedureGroupModal.isActive && (
        <UpdateProcedureGroupModal
          isActive={updateProcedureGroupModal.isActive}
          onClose={updateProcedureGroupModal.close}
          downloadCsvForUpdateGroup={onDownloadCsvForUpdateGroup}
          bulkUpdateGroupPreview={bulkUpdateGroupPreview}
          bulkUpdateGroup={bulkUpdateGroup}
          orgId={procedures[0]?.orgId}
        />
      )}
    </SmartApplyTemplate>
  );
};

export const ProceduresContainer = connectPageService(
  ProceduresList,
  () => new ProceduresService(),
);
